import { Link } from 'react-router-dom';
import ResourceLayout from '../../components/resources/ResourceLayout';
import useSEO from '../../hooks/useSEO';
import { canonicalUrl } from '../../utils/testSite';
import { guidePageSchema } from '../../utils/schema';

const ARTICLE_SCHEMA = {
  '@type': 'Article',
  headline: 'How Fast Can You Finish 24 Hours of Arizona CE?',
  description: 'ADRE caps CE at 9 hours per day, so 24 hours takes a minimum of 3 days. The realistic fastest schedule, explained.',
  datePublished: '2026-06-01',
  dateModified: '2026-06-10',
  author: { '@type': 'Organization', name: 'Steady Education' },
  publisher: {
    '@type': 'EducationalOrganization',
    name: 'Steady Education',
    url: 'https://steadyeducation.com',
  },
};

const RELATED = [
  { url: '/arizona-ce-provider-comparison', title: 'Best Arizona CE Providers (Compared)', desc: 'Every major ADRE-approved provider for 2026, online vs classroom.' },
  { url: '/arizona-real-estate-ce-requirements', title: 'Arizona CE Requirements 2026', desc: 'The complete 24-hour breakdown by category.' },
  { url: '/arizona-ce-2025-rule-changes', title: '2025 Rule Changes', desc: 'Water, Firewise, Deed Fraud — the three new mandatory topics.' },
  { url: '/arizona-ce-online', title: 'Can I Do All My CE Online?', desc: 'Yes — and online is the fastest path.' },
];

export default function NineHourRulePage() {
  useSEO({
    title: 'How Fast Can You Finish 24 Hours of Arizona CE?',
    description: 'ADRE caps CE at 9 hours per day, so 24 hours takes a minimum of 3 days. The realistic fastest schedule, explained.',
    url: canonicalUrl('/how-fast-can-i-finish-arizona-ce'),
    jsonLd: guidePageSchema(ARTICLE_SCHEMA, {
      title: 'How Fast Can You Finish 24 Hours?',
      url: canonicalUrl('/how-fast-can-i-finish-arizona-ce'),
    }),
  });

  return (
    <ResourceLayout
      breadcrumb="9-Hour Rule"
      title="Can You Finish All 24 Hours of Arizona CE in a Weekend? (Almost.)"
      subtitle="Short answer: the minimum is three days. ADRE limits licensees to 9 credit hours in any 24-hour period — so 24 hours of CE physically cannot be done in one or two days, no matter how much coffee is involved."
      relatedGuides={RELATED}
    >
      <h2>The fastest legal schedule</h2>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr><th>Day</th><th>Hours</th><th>Example</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>Day 1</strong></td><td>9</td><td>Agency Law (3) + Contract Law (3) + Commissioner's Standards (3)</td></tr>
            <tr><td><strong>Day 2</strong></td><td>9</td><td>Disclosure (3) + Fair Housing (3) + Legal Issues (3)</td></tr>
            <tr><td><strong>Day 3</strong></td><td>6</td><td>Water/Firewise/Deed Fraud (3) + elective (3)</td></tr>
          </tbody>
        </table>
      </div>

      <h2>What this means if your deadline is close</h2>
      <p>If your license expires Friday, you needed to start Wednesday. Procrastination has a hard floor in Arizona — this is the rule that catches agents who planned to "knock it out the day before."</p>

      <h2>Self-paced online courses are built for exactly this</h2>
      <p>No scheduled class times means Day 1 can start at 9 PM tonight. Steady Education courses run on any device, save your progress automatically, and issue certificates the moment you pass each exam (70% to pass, retakes available) — so you can submit hours to ADRE's portal the same night.</p>

      <p>One more deadline buffer to remember: after finishing courses you still need to <strong>enter each certificate in your ADRE Licensee Login yourself</strong> before you can renew. Leave yourself at least a day for that step.</p>

      <div style={inlineCta}>
        Three days. $49.99. Done.
        <div style={{ marginTop: 14 }}>
          <Link to="/arizona/courses" style={inlineCtaBtn}>Get all 24 hours →</Link>
        </div>
      </div>

      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', fontStyle: 'italic', marginTop: 32 }}>
        Source: ADRE Education Division FAQ (azre.gov).
      </p>
    </ResourceLayout>
  );
}

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 15, border: '1px solid #e8e2d6', borderRadius: 8, overflow: 'hidden' };
const inlineCta = { background: '#FAF7F2', border: '1px solid #d4c5ad', borderRadius: 12, padding: '20px 24px', marginTop: 24 };
const inlineCtaBtn = { display: 'inline-block', background: '#2C453E', color: 'white', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 };
